import { CdkDragEnd, DragDropModule, DragRef, Point } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import { WidgetModel } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { map, Observable } from 'rxjs'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
@Component({
  selector: 'gb-presenter-widget-wrapper',
  imports: [CommonModule, DragDropModule],
  templateUrl: './gb-presenter-widget-wrapper.component.html',
  styleUrl: './gb-presenter-widget-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbPresenterWidgetWrapperComponent implements OnDestroy {
  @Input({ required: true }) widget!: WidgetModel
  @Input() draggable = true
  @Input() zoomLevel = signal<number>(1)
  @Input() disableBackground = false
  @Input() forceOnTop = false
  @Input() lockAspectRatio = false
  @Input() cornerOnlyResize = false

  @Output() positionChanged = new EventEmitter<WidgetModel>()
  @Output() resizeStarted = new EventEmitter<{ widget: WidgetModel; direction: string; event: MouseEvent }>()
  @Output() rotateStarted = new EventEmitter<{ widget: WidgetModel; event: MouseEvent }>()

  @ViewChild('wrapperEl', { static: true }) wrapperEl!: ElementRef<HTMLElement>

  isResizing = false

  private activeResizeCleanup: (() => void) | null = null
  private resizeRafId: number | null = null
  private pendingResizeWidget: WidgetModel | null = null

  isSelected$: Observable<boolean> = this.presenterWidgetsFacade.selectedWidget$.pipe(
    map((widget) => widget?.uuid === this.widget.uuid),
  )

  snapThreshold = 4
  showGuidesX = false
  showGuidesY = false
  showGuideLeft = false
  showGuideRight = false
  showGuideTop = false
  showGuideBottom = false

  private get effectiveWidget(): WidgetModel | null {
    return this.pendingResizeWidget || this.widget || null
  }

  private getIntrinsicImageAspectRatio(): number | null {
    const host = this.wrapperEl?.nativeElement
    if (!host) {
      return null
    }

    const img = host.querySelector('img') as HTMLImageElement | null
    if (img) {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        return img.naturalWidth / img.naturalHeight
      }

      return null
    }

    const svg = host.querySelector('svg') as SVGSVGElement | null
    const viewBox = svg?.viewBox?.baseVal
    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      return viewBox.width / viewBox.height
    }

    return null
  }

  private applyCornerAspectRatioLock(params: {
    direction: 'nw' | 'ne' | 'sw' | 'se'
    aspectRatio: number
    startLeft: number
    startTop: number
    startRight: number
    startBottom: number
    startWidthPx: number
    startHeightPx: number
    dxPx: number
    dyPx: number
    minCssPx: number
  }): { leftPx: number; topPx: number; widthPx: number; heightPx: number } {
    const {
      direction,
      aspectRatio,
      startLeft,
      startTop,
      startRight,
      startBottom,
      startWidthPx,
      startHeightPx,
      dxPx,
      dyPx,
      minCssPx,
    } = params

    const absDx = Math.abs(dxPx)
    const absDy = Math.abs(dyPx)

    let widthPx = startWidthPx
    let heightPx = startHeightPx

    if (absDx >= absDy) {
      const proposedWidth = startWidthPx + (direction === 'sw' || direction === 'nw' ? -dxPx : dxPx)
      widthPx = Math.max(minCssPx, proposedWidth)
      heightPx = Math.max(minCssPx, widthPx / aspectRatio)
    } else {
      const proposedHeight = startHeightPx + (direction === 'ne' || direction === 'nw' ? -dyPx : dyPx)
      heightPx = Math.max(minCssPx, proposedHeight)
      widthPx = Math.max(minCssPx, heightPx * aspectRatio)
    }

    let leftPx = startLeft
    let topPx = startTop

    if (direction === 'sw' || direction === 'nw') {
      leftPx = startRight - widthPx
    }
    if (direction === 'ne' || direction === 'nw') {
      topPx = startBottom - heightPx
    }

    return { leftPx, topPx, widthPx, heightPx }
  }

  get wrapperStyle() {
    const widget = this.effectiveWidget
    if (!widget) return {}

    const x = widget.x ?? 0
    const y = widget.y ?? 0
    const xUnit = widget.x_unit ?? '%'
    const yUnit = widget.y_unit ?? '%'
    const widthUnit = widget.width_unit ?? '%'
    const heightUnit = widget.height_unit ?? '%'

    const style: Record<string, string | number> = {
      position: 'absolute',
      left: `${x}${xUnit}`,
      top: `${y}${yUnit}`,
      zIndex: this.forceOnTop ? 2147483647 : widget.z || 1,
      transform: widget.rotate_z ? `rotate(${widget.rotate_z}${widget.rotate_z_unit})` : 'none',
      backgroundColor: this.disableBackground
        ? 'transparent'
        : this.colorResolver.resolve(widget.background_color) || 'transparent',
      opacity: widget.opacity ?? 1,
      // padding: this.widget.padding ? `${this.widget.padding}${this.widget.padding_unit}` : '8px',
    }

    if (widget.width) {
      style['width'] = this.getDimension(widget.width, widthUnit)
    }
    if (widget.height) {
      style['height'] = this.getDimension(widget.height, heightUnit)
    }

    return style
  }

  constructor(
    private presenterWidgetsFacade: PresenterWidgetsFacade,
    private cdr: ChangeDetectorRef,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  ngOnDestroy(): void {
    this.activeResizeCleanup?.()
    this.activeResizeCleanup = null
    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId)
      this.resizeRafId = null
    }
  }

  private setGuideFlags(flags: {
    showGuidesX: boolean
    showGuidesY: boolean
    showGuideLeft: boolean
    showGuideRight: boolean
    showGuideTop: boolean
    showGuideBottom: boolean
  }): void {
    const guidesChanged =
      flags.showGuidesX !== this.showGuidesX ||
      flags.showGuidesY !== this.showGuidesY ||
      flags.showGuideLeft !== this.showGuideLeft ||
      flags.showGuideRight !== this.showGuideRight ||
      flags.showGuideTop !== this.showGuideTop ||
      flags.showGuideBottom !== this.showGuideBottom

    if (!guidesChanged) {
      return
    }

    this.showGuidesX = flags.showGuidesX
    this.showGuidesY = flags.showGuidesY
    this.showGuideLeft = flags.showGuideLeft
    this.showGuideRight = flags.showGuideRight
    this.showGuideTop = flags.showGuideTop
    this.showGuideBottom = flags.showGuideBottom
    this.cdr.markForCheck()
  }

  private clearAllGuides(): void {
    if (
      this.showGuidesX ||
      this.showGuidesY ||
      this.showGuideLeft ||
      this.showGuideRight ||
      this.showGuideTop ||
      this.showGuideBottom
    ) {
      this.showGuidesX = false
      this.showGuidesY = false
      this.showGuideLeft = false
      this.showGuideRight = false
      this.showGuideTop = false
      this.showGuideBottom = false
      this.cdr.markForCheck()
    }
  }

  constrainPosition = (point: Point, _dragRef: DragRef, _dimensions: ClientRect, pickup: Point): Point => {
    const canvas = document.querySelector('.projector-canvas') as HTMLElement
    const x = point.x - pickup.x
    const y = point.y - pickup.y
    const actualPoint = { x, y }
    if (!canvas) return actualPoint

    const dragEl = _dragRef.getRootElement()
    const dragRect = dragEl.getBoundingClientRect()
    const widgetWidth = dragRect.width
    const widgetHeight = dragRect.height

    const canvasRect = canvas.getBoundingClientRect()
    const centerX = canvasRect.left + canvasRect.width / 2
    const centerY = canvasRect.top + canvasRect.height / 2

    const canvasLeft = canvasRect.left
    const canvasRight = canvasRect.left + canvasRect.width
    const canvasTop = canvasRect.top
    const canvasBottom = canvasRect.top + canvasRect.height

    const widgetCenterX = actualPoint.x + widgetWidth / 2
    const widgetCenterY = actualPoint.y + widgetHeight / 2

    const widgetLeft = actualPoint.x
    const widgetRight = actualPoint.x + widgetWidth
    const widgetTop = actualPoint.y
    const widgetBottom = actualPoint.y + widgetHeight

    const distanceX = Math.abs(widgetCenterX - centerX)
    const distanceY = Math.abs(widgetCenterY - centerY)

    const distanceLeft = Math.abs(widgetLeft - canvasLeft)
    const distanceRight = Math.abs(widgetRight - canvasRight)
    const distanceTop = Math.abs(widgetTop - canvasTop)
    const distanceBottom = Math.abs(widgetBottom - canvasBottom)

    const finalPoint = { ...actualPoint }

    const nextShowGuidesX = distanceX < this.snapThreshold
    const nextShowGuidesY = distanceY < this.snapThreshold

    const nextShowGuideLeft = distanceLeft < this.snapThreshold
    const nextShowGuideRight = distanceRight < this.snapThreshold
    const nextShowGuideTop = distanceTop < this.snapThreshold
    const nextShowGuideBottom = distanceBottom < this.snapThreshold

    if (distanceX < this.snapThreshold) {
      finalPoint.x = centerX - widgetWidth / 2
    }

    if (distanceY < this.snapThreshold) {
      finalPoint.y = centerY - widgetHeight / 2
    }

    if (nextShowGuideLeft || nextShowGuideRight) {
      if (distanceLeft <= distanceRight && nextShowGuideLeft) {
        finalPoint.x = canvasLeft
      } else if (nextShowGuideRight) {
        finalPoint.x = canvasRight - widgetWidth
      }
    }

    if (nextShowGuideTop || nextShowGuideBottom) {
      if (distanceTop <= distanceBottom && nextShowGuideTop) {
        finalPoint.y = canvasTop
      } else if (nextShowGuideBottom) {
        finalPoint.y = canvasBottom - widgetHeight
      }
    }

    this.setGuideFlags({
      showGuidesX: nextShowGuidesX,
      showGuidesY: nextShowGuidesY,
      showGuideLeft: nextShowGuideLeft,
      showGuideRight: nextShowGuideRight,
      showGuideTop: nextShowGuideTop,
      showGuideBottom: nextShowGuideBottom,
    })

    return finalPoint
  }

  getDimension(value: number, unit: string | undefined): string {
    const resolvedUnit = unit ?? '%'
    let modifiedValue = value
    let modifiedUnit = resolvedUnit

    if (resolvedUnit === 'px') {
      modifiedValue = value / 10
      modifiedUnit = 'cqw'
    }

    return `${modifiedValue}${modifiedUnit}`
  }

  private convertPxDeltaToUnit(deltaPx: number, unit: string | undefined, referencePx: number): number {
    const resolvedUnit = unit ?? '%'
    if (resolvedUnit === 'px') {
      return deltaPx
    }

    if (resolvedUnit === '%') {
      if (!referencePx) {
        return 0
      }

      return (deltaPx / referencePx) * 100
    }

    return deltaPx
  }

  private getWidgetDimensionFromRect(cssPx: number, unit: string | undefined, referencePx: number): number {
    const resolvedUnit = unit ?? '%'
    if (resolvedUnit === 'px') {
      return cssPx * 10
    }

    if (resolvedUnit === '%') {
      if (!referencePx) {
        return 0
      }
      return (cssPx / referencePx) * 100
    }

    return cssPx
  }

  private clampDimension(value: number, minValue: number): number {
    if (Number.isNaN(value)) {
      return minValue
    }
    return Math.max(minValue, value)
  }

  onDragEnded(event: CdkDragEnd): void {
    if (!this.draggable) {
      event.source.reset()
      return
    }

    if (!this.widget) return

    const transform = event.source.getFreeDragPosition()

    const dragEl = event.source.element.nativeElement as HTMLElement
    const parentEl = (dragEl.offsetParent as HTMLElement | null) ?? dragEl.parentElement
    const parentRect = parentEl?.getBoundingClientRect()

    const deltaX = (transform.x / (parentRect?.width ?? 1)) * 100
    const deltaY = (transform.y / (parentRect?.height ?? 1)) * 100

    const currentX =
      this.widget.x_unit === 'px' ? ((this.widget.x ?? 0) / (parentRect?.width ?? 1)) * 100 : (this.widget.x ?? 0)
    const currentY =
      this.widget.y_unit === 'px' ? ((this.widget.y ?? 0) / (parentRect?.height ?? 1)) * 100 : (this.widget.y ?? 0)

    const newX = currentX + deltaX
    const newY = currentY + deltaY

    this.positionChanged.emit({
      ...this.widget,
      x: newX,
      x_unit: '%',
      y: newY,
      y_unit: '%',
    })

    this.clearAllGuides()

    event.source.reset()
  }

  onResizeStart(event: MouseEvent, direction: string): void {
    event.preventDefault()
    event.stopPropagation()

    if (!this.widget) return

    this.resizeStarted.emit({
      widget: this.widget,
      direction,
      event,
    })
  }

  onResizePointerDown(event: PointerEvent, direction: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w'): void {
    event.preventDefault()
    event.stopPropagation()

    if (!this.draggable) {
      return
    }

    if (!this.widget) {
      return
    }

    this.activeResizeCleanup?.()
    this.activeResizeCleanup = null

    const target = event.target as HTMLElement | null
    try {
      target?.setPointerCapture(event.pointerId)
    } catch {
      // ignore
    }

    const wrapper = this.wrapperEl?.nativeElement
    const parentEl = (wrapper?.offsetParent as HTMLElement | null) ?? wrapper?.parentElement
    const parentRect = parentEl?.getBoundingClientRect()
    const wrapperRect = wrapper?.getBoundingClientRect()

    const parentWidth = parentRect?.width ?? 0
    const parentHeight = parentRect?.height ?? 0

    const viewportParentRect = parentRect
    const viewportStartRect = wrapperRect

    const parentLeft = viewportParentRect?.left ?? 0
    const parentTop = viewportParentRect?.top ?? 0
    const parentRight = (viewportParentRect?.left ?? 0) + (viewportParentRect?.width ?? 0)
    const parentBottom = (viewportParentRect?.top ?? 0) + (viewportParentRect?.height ?? 0)
    const parentCenterX = parentLeft + parentWidth / 2
    const parentCenterY = parentTop + parentHeight / 2

    const start = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: this.widget.x ?? 0,
      y: this.widget.y ?? 0,
      width:
        this.widget.width ??
        this.getWidgetDimensionFromRect(wrapperRect?.width ?? 0, this.widget.width_unit ?? '%', parentWidth),
      height:
        this.widget.height ??
        this.getWidgetDimensionFromRect(wrapperRect?.height ?? 0, this.widget.height_unit ?? '%', parentHeight),
    }

    const minCssPx = 24
    const minWidth = this.getWidgetDimensionFromRect(minCssPx, this.widget.width_unit ?? '%', parentWidth)
    const minHeight = this.getWidgetDimensionFromRect(minCssPx, this.widget.height_unit ?? '%', parentHeight)

    const aspectRatio = this.lockAspectRatio ? this.getIntrinsicImageAspectRatio() : null
    const shouldLockAspectRatio =
      this.lockAspectRatio &&
      aspectRatio !== null &&
      (direction === 'nw' || direction === 'ne' || direction === 'sw' || direction === 'se')

    this.isResizing = true

    const onMove = (moveEvent: PointerEvent) => {
      if (!this.isResizing) {
        return
      }

      const dxPx = moveEvent.clientX - start.pointerX
      const dyPx = moveEvent.clientY - start.pointerY

      // Calculate proposed rect in viewport pixels
      const startLeft = viewportStartRect?.left ?? 0
      const startTop = viewportStartRect?.top ?? 0
      const startRight = viewportStartRect?.right ?? startLeft
      const startBottom = viewportStartRect?.bottom ?? startTop
      const startWidthPx = viewportStartRect?.width ?? 0
      const startHeightPx = viewportStartRect?.height ?? 0

      let nextLeftPx = startLeft
      let nextTopPx = startTop
      let nextWidthPx = startWidthPx
      let nextHeightPx = startHeightPx

      switch (direction) {
        case 'se':
          nextWidthPx = startWidthPx + dxPx
          nextHeightPx = startHeightPx + dyPx
          break
        case 'sw': {
          const fixedRight = startRight
          nextLeftPx = startLeft + dxPx
          nextWidthPx = fixedRight - nextLeftPx
          nextHeightPx = startHeightPx + dyPx
          break
        }
        case 'ne': {
          const fixedBottom = startBottom
          nextTopPx = startTop + dyPx
          nextHeightPx = fixedBottom - nextTopPx
          nextWidthPx = startWidthPx + dxPx
          break
        }
        case 'nw': {
          const fixedRight = startRight
          const fixedBottom = startBottom
          nextLeftPx = startLeft + dxPx
          nextTopPx = startTop + dyPx
          nextWidthPx = fixedRight - nextLeftPx
          nextHeightPx = fixedBottom - nextTopPx
          break
        }
        case 'n': {
          const fixedBottom = startBottom
          nextTopPx = startTop + dyPx
          nextHeightPx = fixedBottom - nextTopPx
          break
        }
        case 'e':
          nextWidthPx = startWidthPx + dxPx
          break
        case 's':
          nextHeightPx = startHeightPx + dyPx
          break
        case 'w': {
          const fixedRight = startRight
          nextLeftPx = startLeft + dxPx
          nextWidthPx = fixedRight - nextLeftPx
          break
        }
      }

      if (shouldLockAspectRatio) {
        const locked = this.applyCornerAspectRatioLock({
          direction,
          aspectRatio,
          startLeft,
          startTop,
          startRight,
          startBottom,
          startWidthPx,
          startHeightPx,
          dxPx,
          dyPx,
          minCssPx,
        })

        nextLeftPx = locked.leftPx
        nextTopPx = locked.topPx
        nextWidthPx = locked.widthPx
        nextHeightPx = locked.heightPx
      }

      // Clamp minimum size in px
      nextWidthPx = Math.max(minCssPx, nextWidthPx)
      nextHeightPx = Math.max(minCssPx, nextHeightPx)

      const nextRightPx = nextLeftPx + nextWidthPx
      const nextBottomPx = nextTopPx + nextHeightPx

      if (shouldLockAspectRatio) {
        this.setGuideFlags({
          showGuidesX: false,
          showGuidesY: false,
          showGuideLeft: false,
          showGuideRight: false,
          showGuideTop: false,
          showGuideBottom: false,
        })
      }

      const movingX = direction === 'se' || direction === 'ne' || direction === 'e' ? nextRightPx : nextLeftPx
      const movingY = direction === 'se' || direction === 'sw' || direction === 's' ? nextBottomPx : nextTopPx

      const distanceCenterX = Math.abs(movingX - parentCenterX)
      const distanceCenterY = Math.abs(movingY - parentCenterY)
      const distanceLeft = Math.abs(nextLeftPx - parentLeft)
      const distanceRight = Math.abs(nextRightPx - parentRight)
      const distanceTop = Math.abs(nextTopPx - parentTop)
      const distanceBottom = Math.abs(nextBottomPx - parentBottom)

      const snapCenterX = distanceCenterX < this.snapThreshold
      const snapCenterY = distanceCenterY < this.snapThreshold
      const snapLeft = distanceLeft < this.snapThreshold
      const snapRight = distanceRight < this.snapThreshold
      const snapTop = distanceTop < this.snapThreshold
      const snapBottom = distanceBottom < this.snapThreshold

      if (!shouldLockAspectRatio) {
        // Apply snapping in px space based on which edges are movable for this handle.
        // Snap the active handle (moving edges), not the widget center.
        if (snapCenterX) {
          if (direction === 'se' || direction === 'ne' || direction === 'e') {
            // right edge is moving; left is fixed
            nextWidthPx = Math.max(minCssPx, parentCenterX - nextLeftPx)
          } else {
            // left edge is moving; right is fixed
            const fixedRight = nextRightPx
            nextLeftPx = parentCenterX
            nextWidthPx = Math.max(minCssPx, fixedRight - nextLeftPx)
          }
        }

        if (snapCenterY) {
          if (direction === 'se' || direction === 'sw' || direction === 's') {
            // bottom edge is moving; top is fixed
            nextHeightPx = Math.max(minCssPx, parentCenterY - nextTopPx)
          } else {
            // top edge is moving; bottom is fixed
            const fixedBottom = nextBottomPx
            nextTopPx = parentCenterY
            nextHeightPx = Math.max(minCssPx, fixedBottom - nextTopPx)
          }
        }

        if (snapLeft && (direction === 'sw' || direction === 'nw' || direction === 'w')) {
          const fixedRight = nextLeftPx + nextWidthPx
          nextLeftPx = parentLeft
          nextWidthPx = fixedRight - nextLeftPx
          nextWidthPx = Math.max(minCssPx, nextWidthPx)
        }

        if (snapRight && (direction === 'se' || direction === 'ne' || direction === 'e')) {
          nextWidthPx = Math.max(minCssPx, parentRight - nextLeftPx)
        }

        if (snapTop && (direction === 'ne' || direction === 'nw' || direction === 'n')) {
          const fixedBottom = nextTopPx + nextHeightPx
          nextTopPx = parentTop
          nextHeightPx = fixedBottom - nextTopPx
          nextHeightPx = Math.max(minCssPx, nextHeightPx)
        }

        if (snapBottom && (direction === 'se' || direction === 'sw' || direction === 's')) {
          nextHeightPx = Math.max(minCssPx, parentBottom - nextTopPx)
        }
      }

      // Convert snapped viewport rect to parent-local px
      const localLeftPx = nextLeftPx - parentLeft
      const localTopPx = nextTopPx - parentTop

      // Convert local px sizes to widget units
      const xUnit = this.widget.x_unit ?? '%'
      const yUnit = this.widget.y_unit ?? '%'
      const widthUnit = this.widget.width_unit ?? '%'
      const heightUnit = this.widget.height_unit ?? '%'

      let nextX = parentWidth ? (localLeftPx / parentWidth) * 100 : 0
      let nextY = parentHeight ? (localTopPx / parentHeight) * 100 : 0

      let nextW = this.getWidgetDimensionFromRect(nextWidthPx, widthUnit, parentWidth)
      let nextH = this.getWidgetDimensionFromRect(nextHeightPx, heightUnit, parentHeight)

      const clampedW = this.clampDimension(nextW, minWidth)
      const clampedH = this.clampDimension(nextH, minHeight)

      if (clampedW !== nextW) {
        if (direction === 'nw' || direction === 'sw' || direction === 'w') {
          const diff = nextW - clampedW
          const diffPx = widthUnit === 'px' ? diff / 10 : widthUnit === '%' ? (diff / 100) * parentWidth : diff
          nextX =
            (this.widget.x_unit === 'px' ? ((this.widget.x ?? 0) / parentWidth) * 100 : (this.widget.x ?? 0)) +
            ((dxPx - diffPx) / parentWidth) * 100
        }
        nextW = clampedW
      }

      if (clampedH !== nextH) {
        if (direction === 'nw' || direction === 'ne' || direction === 'n') {
          const diff = nextH - clampedH
          const diffPx = heightUnit === 'px' ? diff / 10 : heightUnit === '%' ? (diff / 100) * parentHeight : diff
          nextY =
            (this.widget.y_unit === 'px' ? ((this.widget.y ?? 0) / parentHeight) * 100 : (this.widget.y ?? 0)) +
            ((dyPx - diffPx) / parentHeight) * 100
        }
        nextH = clampedH
      }

      if (!shouldLockAspectRatio) {
        this.setGuideFlags({
          showGuidesX: snapCenterX,
          showGuidesY: snapCenterY,
          showGuideLeft: snapLeft,
          showGuideRight: snapRight,
          showGuideTop: snapTop,
          showGuideBottom: snapBottom,
        })
      }

      this.queueResizeEmit({
        ...this.widget,
        x: nextX,
        x_unit: '%',
        y: nextY,
        y_unit: '%',
        width: nextW,
        height: nextH,
      })
    }

    const onUp = () => {
      this.isResizing = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      this.activeResizeCleanup = null

      this.clearAllGuides()

      try {
        target?.releasePointerCapture(event.pointerId)
      } catch {
        // ignore
      }

      if (this.pendingResizeWidget) {
        const finalWidget = this.pendingResizeWidget
        this.pendingResizeWidget = null
        this.positionChanged.emit(finalWidget)
        this.cdr.markForCheck()
      }
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    this.activeResizeCleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      this.isResizing = false
    }
  }

  private queueResizeEmit(widget: WidgetModel): void {
    this.pendingResizeWidget = widget
    if (this.resizeRafId !== null) {
      return
    }
    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = null
      this.cdr.markForCheck()
    })
  }

  onRotateStart(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()

    if (!this.widget) return

    this.rotateStarted.emit({
      widget: this.widget,
      event,
    })
  }
}
